import { Injectable } from '@angular/core';
import {
  Resolve,
  ActivatedRouteSnapshot
} from '@angular/router';
import { UserByUnitList } from '@app/models/profile.model';
import { CareConneRoomItem, CareConneRoomListByUser, UserByUnit } from '@app/models/user.model';
import { ApiService } from '@app/services/api.service';
import { blockCd, corpCd, createObjCd, officeCd } from '@app/shared/common/utils';
import { DataSelectors } from '@app/store/data/data.selectors';
import { SetCareConneRoomList } from '@app/store/data/data.classes';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { Store } from '@ngxs/store';
import { catchError, forkJoin, map, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CareconneRoomByStudentResolver implements Resolve<Record<string, UserByUnit>> {
  userByUnitList: UserByUnitList[] = [];
  renderedDataNote: any = {};

  constructor(
    private apiService: ApiService,
    private store: Store
  ) { }
  resolve(route: ActivatedRouteSnapshot): Observable<Record<string, UserByUnit>> {
    let pass = this.store.selectSnapshot(SettingSelectors.CareConnePass);
    const daicds = route.queryParamMap.getAll('daicds') || [];
    this.userByUnitList = this.store.selectSnapshot(DataSelectors.userByUnitList);
    this.renderedDataNote = this.store.selectSnapshot(DataSelectors.renderedDataNote);
    return this.getRoomsMap(pass, daicds);
  }

  private getRoomsMap(pass: number, daicds: string[]) {
    const grouped = new Map<string, string[]>();

    // group by daicd
    daicds.forEach((d) => {
      // Current format a daicd equals: ownunitcd#objcd
      const [ownunitcd, objcd] = d.split('#');

      if (!grouped.has(objcd)) {
        grouped.set(objcd, []);
      }

      grouped.get(objcd)!.push(ownunitcd);
    });

    const requests = Array.from(grouped.entries()).map(
      ([objcd, ownunitcds]) => {
        const localData = this.store.selectSnapshot(
          DataSelectors.careConneRoomList(objcd)
        ) as UserByUnit;

        const unitsByDai = this.findUnits(objcd);

        if (!unitsByDai.length) {
          return of({ objcd, data: null });
        }

        const unitMap = new Map(
          unitsByDai.map((u) => [
            `${u.dai.daicd}_${u.unit.objcd}`, // daicd_unitObjcd
            u
          ])
        );

        const mustBeRefetch = this.isExpired(localData);

        const rooms$ = mustBeRefetch
          ? this.apiService.getCareConneRoomList(pass, objcd).pipe(
            catchError(() => of([])),
            map((rooms: CareConneRoomItem[]) => ({
              rooms,
              lifeSpan: Date.now() + 5 * 60 * 1000
            }))
          )
          : of({
            rooms:
              localData?.roomDataByUnit?.flatMap((u) => u.rooms) ?? [],
            lifeSpan: new Date(localData.lifeSpan).getTime()
          });

        return rooms$.pipe(
          map(({ rooms, lifeSpan }) => {
            const roomDataByUnit = unitsByDai.map(({ unit }) =>
              this.buildRoomByUnit(objcd, rooms, unit)
            );

            return ownunitcds
              .map((ownunitcd) => {
                const unitByDai = unitMap.get(
                  `${objcd}_${ownunitcd}`
                );

                if (!unitByDai) return null;

                return {
                  daicd: objcd,
                  data: {
                    lifeSpan: new Date(lifeSpan).toString(),
                    name: this.buildName(unitByDai.dai),
                    daicd: objcd,
                    roomDataByUnit
                  } as UserByUnit
                };
              })
              .filter(
                (item): item is { daicd: string; data: UserByUnit } =>
                  !!item
              );
          })
        );
      }
    );

    return forkJoin(requests).pipe(
      map((results) =>
        results
          .flat()
          .reduce((acc, cur) => {
            if (cur.data) {
              acc[cur.daicd] = cur.data;
            }
            return acc;
          }, {} as Record<string, UserByUnit>)
      ),
      tap((res) => {
        this.store.dispatch(new SetCareConneRoomList(res));
      })
    );
  }

  private buildRoomByUnit(
    daicd: string,
    rooms: CareConneRoomItem[],
    unit: UserByUnitList
  ): CareConneRoomListByUser {
    const objcd = unit.objcd;

    const notes = this.getNotes(daicd, objcd);

    return {
      rooms,
      notes,
      unitName: unit.name,
      objcd
    };
  }

  private buildName(dai: any) {
    return `${dai.family} ${dai.given}`;
  }

  private getNotes(daicd: string, objcd: string) {
    const corpcd = corpCd(objcd);
    const officecd = officeCd(objcd);
    const blockcd = blockCd(objcd);

    const sources = [
      objcd,
      createObjCd(corpcd, officecd, '00', '000'),
      createObjCd(corpcd, officecd, blockcd, '000')
    ];

    const notes = sources.flatMap(
      (src) =>
        this.renderedDataNote[src]?.filter(
          (item: any) => item.objcd === daicd
        ) ?? []
    );

    return this.dedupeNotes(notes);
  }

  private dedupeNotes(notes: any[]) {
    const map = new Map();
    notes.forEach((n) => {
      map.set(n.noteid, n);
    });
    return Array.from(map.values());
  }

  private findUnits(daicd: string) {
    const results: Array<{ unit: UserByUnitList; dai: any }> = [];

    for (let unit of this.userByUnitList) {
      const daiInfo = unit.items.find(
        (item) => item.daicd === daicd
      );
      if (daiInfo) {
        results.push({ unit, dai: daiInfo });
      }
    }

    return results;
  }

  private isExpired(data?: UserByUnit) {
    return (
      !data ||
      !data.lifeSpan ||
      Date.now() > new Date(data.lifeSpan).getTime()
    );
  }
}
